import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import './Resume.scss';

import { Icon, OptionDropdownInner } from '../../../../shared/components/ui/atoms';
import { FooterContent } from '../../../onboarding/components';
import { useGetBorrowers } from '../../../broker/hooks/useBrokerQueries';
import { useOwnership } from '../../context/OwnershipContext';
import { SelectedIndexData } from '../../../../types';
import { debounce } from 'lodash';

interface ResumeProps {
  isSelected?: boolean;
  setBottomValue?: (value: number) => void;
  setSelectedIndex?: (index: number, options?: { force?: boolean }) => void;
  setSelectedIndexData?: (data: SelectedIndexData) => void;
  values: any;
  setValue?: (key: string, value: any) => void;
  setMultipleValues?: (values: Record<string, any>) => void;
  onFinish: () => void;
  isBrokerMode?: boolean;
  setBorrowerData?: (data: any) => void;
  borrowerData?: any;
  isProcessingTeamMode?: boolean;
  isBorrowerMode?: boolean;
  ownerships?: any[];
  stepIndex?: number;
  [key: string]: any;
}

export const Resume = (props: ResumeProps) => {
  const { ownershipSelected, setOwnershipSelected } = useOwnership()
  const { isSelected, setBottomValue, setSelectedIndex, setSelectedIndexData, values, setValue, setMultipleValues, onFinish, isBrokerMode, setBorrowerData, borrowerData, stepIndex } = props;
  const { isProcessingTeamMode, isBorrowerMode, ownerships, dropdownCustomWidths, stateDropdownCustomWidth } = props;

  const stepRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (stepRef.current && isSelected && setBottomValue) {
      setBottomValue(stepRef.current.offsetTop)
    }
  }, [isSelected, setBottomValue])

  const Wrapper = useMemo(() => isProcessingTeamMode || isBorrowerMode ? (props: any) => <div  className='processing-card' {...props} />  : React.Fragment, [isProcessingTeamMode, isBorrowerMode])

  return (
    <div className='step-section resume processing' ref={stepRef} data-step-index={stepIndex !== undefined ? stepIndex : undefined}>
      <Wrapper>
        <div className='icon-container'>
          <Icon svgFilePath={"./assets/icons/icon-coins.svg"} isBrokerMode={isBrokerMode} />
        </div>
        {
          isProcessingTeamMode ?
            <BorrowerSelectionSection setBorrowerData={setBorrowerData} />
            :
            null
        }
        <FooterContent 
          values={values} 
          setSelectedIndex={setSelectedIndex} 
          setSelectedIndexData={setSelectedIndexData} 
          setValue={setValue} 
          setMultipleValues={setMultipleValues} 
          isBrokerMode={isBrokerMode} 
          isProcessingTeamMode={isProcessingTeamMode} 
          borrowerData={borrowerData} 
          ownerships={ownerships}
          dropdownCustomWidths={dropdownCustomWidths}
          stateDropdownCustomWidth={stateDropdownCustomWidth}
        />
        <div className='loan-options-button' onClick={onFinish}>
          {
            !isProcessingTeamMode ?
              "Show me my loan options"
              :
              "Show me loan options for this borrower"
          }
        </div>
      </Wrapper>
    </div>
  );
}

interface BorrowerSelectionSectionProps {
  setBorrowerData?: (data: any) => void;
}

const BorrowerSelectionSection = ({ setBorrowerData }: BorrowerSelectionSectionProps) => {
  const [selectedBorrowerEmail, setSelectedBorrowerEmail] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")
  const isUpdated = useRef(false)

  // Debounce para la búsqueda (500ms)
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchTerm(value)
    }, 500),
    []
  )

  // Limpiar debounce al desmontar
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  const { borrowers, hasMore, loadMore, isLoadingMore, resetPagination } = useGetBorrowers(debouncedSearchTerm)

  const filteredBorrowers = borrowers?.filter((r) => r.name) || []

  // Auto-seleccionar el primer borrower en la carga inicial
  useEffect(() => {
    if (!debouncedSearchTerm && !isUpdated.current && filteredBorrowers.length > 0 && setBorrowerData) {
      const firstBorrower = filteredBorrowers[0]
      if (firstBorrower?.email) {
        setBorrowerData(firstBorrower)
        setSelectedBorrowerEmail(firstBorrower.email)
        isUpdated.current = true
      }
    }
  }, [filteredBorrowers, debouncedSearchTerm, setBorrowerData])

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
    debouncedSetSearch(value)
  }, [debouncedSetSearch])

  const onChange = useCallback((v: string) => {
    setSelectedBorrowerEmail(v)
    const selectedOption = filteredBorrowers.find((b) => b.email === v)
    
    if (selectedOption && setBorrowerData) {
      setBorrowerData(selectedOption)
    } else if (v === "new") {
      setBorrowerData?.(null)
    }
    
    // Resetear búsqueda después de seleccionar
    if (debouncedSearchTerm) {
      setSearchTerm("")
      setDebouncedSearchTerm("")
      debouncedSetSearch.cancel()
      resetPagination()
    }
  }, [filteredBorrowers, setBorrowerData, debouncedSearchTerm, debouncedSetSearch, resetPagination])

  const options: Array<{
    value: string
    label: string
    disabled: boolean
    className?: string
    raw: any
  }> = [
    ...filteredBorrowers.map((v) => ({
      value: v.email ?? '',
      label: `${v.name} ${v.lastName ?? ''}`.trim(),
      disabled: false,
      raw: v
    })),
    {
      value: "new",
      label: "New Borrower",
      disabled: false,
      className: 'new-borrower',
      raw: null
    }
  ]

  return (
    <span>
      New loan for {" "}
      <div style={{ display: 'inline-block' }}>
        <OptionDropdownInner
          isProcessingTeamMode={true}
          onSearch={handleSearch}
          options={options}
          defaultValue={options[0]?.value}
          value={selectedBorrowerEmail ?? undefined}
          onChange={onChange}
          hasMore={hasMore}
          onLoadMore={loadMore}
          isLoadingMore={isLoadingMore}
        />
      </div>
    </span>
  )
}
